/**
 * SaveCompositionDialog Component
 * Dialog for saving compositions to user's library
 */

import { useState, useEffect, useRef } from 'react';
import { announceToScreenReader, trapFocus } from '../../utils/accessibility';
import { CloseButton } from './CloseButton';
import './SaveCompositionDialog.css';

/**
 * Props for SaveCompositionDialog component
 */
interface SaveCompositionDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback to close the dialog */
  onClose: () => void;
  /** Callback when save is confirmed */
  onSave: (title: string, saveAsNew: boolean) => void;
  /** Current composition title (if editing existing) */
  currentTitle?: string;
  /** Whether this is an existing composition */
  isExistingComposition?: boolean;
  /** Whether save operation is in progress */
  isSaving?: boolean;
}

/**
 * SaveCompositionDialog component
 * Provides a form for saving compositions with title input and save options
 */
export function SaveCompositionDialog({
  isOpen,
  onClose,
  onSave,
  currentTitle = '',
  isExistingComposition = false,
  isSaving = false,
}: SaveCompositionDialogProps) {
  const [title, setTitle] = useState(currentTitle);
  const [saveAsNew, setSaveAsNew] = useState(false);
  const [error, setError] = useState('');
  const [isRendered, setIsRendered] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);

  // Update title when currentTitle changes
  useEffect(() => {
    if (isOpen) {
      setTitle(currentTitle);
      setSaveAsNew(false);
      setError('');
      // Delay rendering slightly to ensure CSS is loaded
      setIsRendered(false);
      requestAnimationFrame(() => {
        setIsRendered(true);
      });
    } else {
      setIsRendered(false);
    }
  }, [isOpen, currentTitle]);

  // Focus title input and trap focus when dialog opens
  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll
      const originalOverflow = document.body.style.overflow;
      const originalPosition = document.body.style.position;
      const originalWidth = document.body.style.width;
      
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      
      // Focus input after a brief delay to ensure rendering is complete
      const focusTimeout = setTimeout(() => {
        if (titleInputRef.current) {
          titleInputRef.current.focus();
          titleInputRef.current.select();
        }
      }, 50);
      
      // Announce dialog opening to screen readers
      announceToScreenReader(
        isExistingComposition 
          ? 'Save composition dialog opened. Enter a title and choose save options.'
          : 'Save new composition dialog opened. Enter a title for your composition.',
        'polite'
      );
      
      // Trap focus within dialog
      let cleanup: (() => void) | undefined;
      if (dialogRef.current) {
        cleanup = trapFocus(dialogRef.current);
      }
      
      // Cleanup function
      return () => {
        clearTimeout(focusTimeout);
        document.body.style.overflow = originalOverflow;
        document.body.style.position = originalPosition;
        document.body.style.width = originalWidth;
        if (cleanup) cleanup();
      };
    }
    return undefined;
  }, [isOpen, isExistingComposition]);

  // Handle escape key to close dialog
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isSaving) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
    return undefined;
  }, [isOpen, isSaving]);

  // Handle click outside dialog to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(e.target as Node) && !isSaving) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [isOpen, isSaving]);

  /**
   * Handle dialog close
   */
  const handleClose = () => {
    if (!isSaving) {
      setTitle('');
      setError('');
      setSaveAsNew(false);
      onClose();
    }
  };

  /**
   * Validate title
   */
  const validateTitle = (titleValue: string): string | null => {
    if (!titleValue.trim()) {
      return 'Title is required';
    }

    if (titleValue.length > 100) {
      return 'Title must be 100 characters or less';
    }

    return null;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate title
    const validationError = validateTitle(title);
    if (validationError) {
      setError(validationError);
      announceToScreenReader(`Error: ${validationError}`, 'assertive');
      return;
    }

    // Announce save action
    announceToScreenReader(
      saveAsNew 
        ? `Saving new composition: ${title.trim()}`
        : `Updating composition: ${title.trim()}`,
      'polite'
    );

    // Call onSave with title and save option
    onSave(title.trim(), saveAsNew);
  };

  if (!isOpen || !isRendered) return null;

  return (
    <div
      className="preset-dialog-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="save-composition-dialog-title"
    >
      <div 
        className="preset-dialog" 
        ref={dialogRef}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <div className="preset-dialog-header">
          <h2 id="save-composition-dialog-title" className="preset-dialog-title">
            {isExistingComposition ? 'Save Composition' : 'Save New Composition'}
          </h2>
          <CloseButton
            onClick={handleClose}
            ariaLabel="Close dialog"
            size="medium"
            variant="minimal"
          />
        </div>

        <form className="preset-dialog-form" onSubmit={handleSubmit}>
          <div className="preset-form-group">
            <label htmlFor="composition-title" className="preset-form-label">
              Composition Title <span className="preset-form-required">*</span>
            </label>
            <input
              ref={titleInputRef}
              id="composition-title"
              type="text"
              className="preset-form-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Battle Theme, Menu Music"
              maxLength={100}
              required
              disabled={isSaving}
              aria-required="true"
              aria-invalid={!!error}
              aria-describedby={error ? 'composition-error' : undefined}
            />
            <span className="preset-form-hint">{title.length}/100 characters</span>
          </div>

          {isExistingComposition && (
            <div className="preset-form-group">
              <label className="preset-form-checkbox-label">
                <input
                  type="checkbox"
                  checked={saveAsNew}
                  onChange={(e) => setSaveAsNew(e.target.checked)}
                  disabled={isSaving}
                />
                <span style={{ marginLeft: '0.5rem' }}>Save as new composition</span>
              </label>
              <span className="preset-form-hint">
                {saveAsNew
                  ? 'A new composition will be created'
                  : 'The existing composition will be updated'}
              </span>
            </div>
          )}

          {error && (
            <div id="composition-error" className="preset-form-error" role="alert">
              {error}
            </div>
          )}

          <div className="preset-dialog-actions">
            <button
              type="button"
              className="preset-btn preset-btn-secondary"
              onClick={handleClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button type="submit" className="preset-btn preset-btn-primary" disabled={isSaving}>
              {isSaving ? 'Saving...' : isExistingComposition && !saveAsNew ? 'Update' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
